import type { Selector } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { groupBy, keys } from "lodash";

import { OrderCheckoutType } from "@/shop/types/shopping/order";
import { createPartialDeliveryNo } from "@/shop/utils/createPartialDeliveryNo";

import type { IOrderReducerState } from "./types";
import { getSessionData } from "../../session/selectors";
import type { IShopRootState } from "../../types";

// import { getIsOciSession } from "../../session";

const orderStateSelector = (state: IShopRootState): IOrderReducerState => state.shopping.order;

// commissions

const commissionsStateSelector = createSelector(orderStateSelector, (state) => state.commissions);

export const getCommissionsList = createSelector(commissionsStateSelector, (state) => state.commissionsList);

export const getCommissionsIsFetching = createSelector(commissionsStateSelector, (state) => state.isFetching);

export const getCommissionsHasError = createSelector(commissionsStateSelector, (state) => state.hasError);

// price request

const priceRequestStateSelector = createSelector(orderStateSelector, (state) => state.priceRequest);

export const getPriceRequest = createSelector(priceRequestStateSelector, (state) => state.priceRequest);

export const getPriceRequestProgress = createSelector(priceRequestStateSelector, (state) => state.progress);

export const getPriceRequestIsFetching = createSelector(priceRequestStateSelector, (state) => state.isFetching);

/**
 * Price request has error
 * - possible reasons: failed to fetch
 * - error property in price request is set
 */
export const getPriceRequestHasError = createSelector(priceRequestStateSelector, (state) => state.hasError || state.priceRequest?.error != null);

export const getPriceRequestErrorStatusCode = createSelector(priceRequestStateSelector, (state) => state.errorStatusCode);

// confirmation

export const orderConfirmationStateSelector = createSelector(orderStateSelector, (state) => state.confirmation);

export const getOrderConfirmationInfo = createSelector(orderConfirmationStateSelector, (state) => state.order);

export const getOrderConfirmationHasError = createSelector(orderConfirmationStateSelector, (state) => state.hasError);

export const getOrderConfirmationIsFetching = createSelector(orderConfirmationStateSelector, (state) => state.isFetching);

export const getOrderConfirmationErrorStatusCode = createSelector(orderConfirmationStateSelector, (state) => state.errorStatusCode);

// partial delivery

export const getOrderPositions = createSelector(getPriceRequest, (priceRequest) => (priceRequest ? priceRequest.orderPositions : []));

export const getEarliestDeliveryDate = createSelector(getPriceRequest, (priceRequest) =>
  priceRequest ? priceRequest.minDeliveryDate : new Date().toISOString().slice(0, 10)
);

export const getPartialDeliveryNo = createSelector(getPriceRequest, (priceRequest) => (priceRequest ? priceRequest.partialDeliveryNo : null));

const getPartialDeliveryPartitionPositions = createSelector(getOrderPositions, getEarliestDeliveryDate, (positions, minDeliveryDate) =>
  positions.map((pos) => ({
    ...pos,
    requestedDeliveryDate: pos.requestedDeliveryDate || minDeliveryDate
  }))
);

export const getPartialDeliveryDates = createSelector(getPartialDeliveryPartitionPositions, (positions) =>
  keys(groupBy(positions, "requestedDeliveryDate")).sort()
);

export const getPartialDeliveryPositions = createSelector(
  getPartialDeliveryPartitionPositions,
  getPartialDeliveryDates,
  getPartialDeliveryNo,
  (positions, dates, partialDeliveryNo) => {
    return positions.map((pos) => {
      const posIndexByDate = dates.indexOf(pos.requestedDeliveryDate!);
      return {
        ...pos,
        partialDeliveryNo: createPartialDeliveryNo(partialDeliveryNo!, posIndexByDate, positions.length)
      };
    });
  }
);

export const getCheckoutType: Selector<IShopRootState, OrderCheckoutType> = createSelector(getSessionData, (snData) => {
  const { punchoutType, punchoutSessionId } = snData;

  if (punchoutSessionId) {
    if (punchoutType === "oci") {
      return OrderCheckoutType.Oci;
    } else if (punchoutType === "cxml") {
      return OrderCheckoutType.cXml;
    }
  }

  return OrderCheckoutType.Order;
});

export const getIsPriceRequestStarting: Selector<IShopRootState, boolean> = createSelector(priceRequestStateSelector, (state) => state.isStarting);

export const getIsShopBusy: Selector<IShopRootState, boolean> = createSelector(getIsPriceRequestStarting, (isPriceRequestStarting) => isPriceRequestStarting);

export const getIsPriceRequestWaitingForUpdate = createSelector(priceRequestStateSelector, (state) => state.waitingForUpdate);
